import { httpsCallable } from 'firebase/functions';
import type {
  EstimateRequest,
  EstimateResponse,
  ResolveAddressRequest,
  ResolveAddressResponse,
} from '@ks-studio/shared';
import { getFirebaseFunctions } from './firebase';
import { fetchSuggestions, type Suggestion } from './places';
import { IS_MOCK } from './env';

/**
 * The two callables, and a mock that stands in for both when no Firebase config
 * was injected.
 *
 * Request and response types are IMPORTED from `@ks-studio/shared`, never
 * restated here. dac-qualifier declares its own `ResolveResponse` in this file,
 * mirroring the server's by hand; that works until the two drift, and nothing
 * fails when they do. The shared package exists precisely so the wire contract
 * has one definition.
 */

/**
 * Autocomplete predictions for step 1, real or mocked.
 *
 * Branches on `IS_MOCK` for the same reason the two callables do: `npm run dev`
 * has to work with no credentials. Without this, mock mode would have no Maps
 * key, `loadMaps()` would reject on every keystroke, and step 1 would be a dead
 * input with no way to reach any screen behind it.
 */
export async function suggest(input: string): Promise<Suggestion[]> {
  if (IS_MOCK) return mockSuggest(input);
  return fetchSuggestions(input);
}

/**
 * Step 1. Exchanges a Places `placeId` for a signed address token, or a decline.
 *
 * `limitedUseAppCheckTokens` IS SET HERE AND DELIBERATELY NOT ON `estimate`,
 * and the two are not interchangeable. `resolveAddress` declares
 * `consumeAppCheckToken: true`, which requires a limited-use token and consumes
 * it, so a captured token cannot buy more Places calls. A callable that consumes
 * will REJECT a standard token, so dropping this line breaks step 1 outright.
 * `estimate` does not consume, so asking for a limited-use token there would
 * cost an extra attestation round trip for nothing.
 *
 * @throws Whatever the callable rejects with. Step 1 surfaces it as retryable.
 */
export async function resolveAddress(
  req: ResolveAddressRequest,
): Promise<ResolveAddressResponse> {
  if (IS_MOCK) return mockResolve(req.placeId);
  const fns = getFirebaseFunctions()!;
  const call = httpsCallable<ResolveAddressRequest, ResolveAddressResponse>(
    fns,
    'resolveAddress',
    { limitedUseAppCheckTokens: true },
  );
  return (await call(req)).data;
}

/**
 * Step 2. Fires the lead and returns the three numbers step 3 renders.
 *
 * The lead reaches n8n and the CRM as a side effect of this call, so a retry is
 * only safe because `submissionId` is stable across retries and the Sell write
 * is an upsert filtered on it (ADR-0024). Do not mint a fresh id on retry.
 *
 * @throws Whatever the callable rejects with. An expired `addressToken` returns
 *         the customer to step 1 with their contact fields intact.
 */
export async function estimate(req: EstimateRequest): Promise<EstimateResponse> {
  if (IS_MOCK) return mockEstimate(req.addressToken);
  const fns = getFirebaseFunctions()!;
  const call = httpsCallable<EstimateRequest, EstimateResponse>(fns, 'estimate');
  return (await call(req)).data;
}

// ---------------------------------------------------------------------------
// Mock mode. Exported so tests and `npm run dev` can drive it directly.
//
// EVERY ARM OF BOTH RESPONSES IS REACHABLE, and that is the point rather than a
// convenience. In dac-qualifier the non-designated screen had no trigger, so it
// carried unreviewed copy on a live domain for weeks: a screen nobody can reach
// is a screen nobody reviews. Studio's declined arms will carry copy that has to
// be signed off, so each one gets a trigger before the copy is written.
//
// Triggered off `placeId` because mock mode has no Places Autocomplete to
// produce a real one. Task 19 decides how step 1 supplies a placeId with no
// Places session; until then these are driven directly in tests.
// ---------------------------------------------------------------------------

/**
 * Mock step 1 autocomplete.
 *
 * EVERY ARM IS ONE CLICK AWAY, which is the entire point. The placeIds below
 * carry the triggers `mockResolve` and `mockEstimate` dispatch on, so a reviewer
 * with no credentials can reach the served path, all three declines and the
 * coverage miss from the dropdown. dac-qualifier shipped a screen that had no
 * trigger at all, and it carried unreviewed copy on a live domain for weeks.
 *
 * Ignores `input` deliberately: filtering these by what was typed would make the
 * unhappy screens hard to reach again, which is the failure this exists to stop.
 */
export async function mockSuggest(_input: string): Promise<Suggestion[]> {
  return [
    { placeId: 'mock-served', text: '1 Broadway, Menands, NY 12204 (mock: served)' },
    {
      placeId: 'mock-solar-miss',
      text: '2 Broadway, Menands, NY 12204 (mock: coverage miss)',
    },
    {
      placeId: 'mock-out-of-state',
      text: '100 Main St, Cleveland, OH (mock: out of state)',
    },
    {
      placeId: 'mock-out-of-area',
      text: '200 Main St, Hempstead, NY (mock: out of area)',
    },
    { placeId: 'mock-location-unknown', text: 'Somewhere, NY (mock: location unknown)' },
  ];
}

/** placeId substring -> the arm it selects. Anything else resolves as served. */
const MOCK_TRIGGERS = {
  outOfState: 'out-of-state',
  outOfArea: 'out-of-area',
  unknown: 'location-unknown',
  miss: 'solar-miss',
} as const;

/** The Menands office, matching the committed Place Details fixture. */
const MOCK_SERVED = {
  formatted: '1 Broadway, Menands, NY 12204, USA',
  county: 'Albany',
  lat: 42.6803,
  lng: -73.7262,
} as const;

/** Mock step 1. */
export async function mockResolve(placeId: string): Promise<ResolveAddressResponse> {
  const p = placeId.toLowerCase();
  if (p.includes(MOCK_TRIGGERS.outOfState))
    return { served: false, reason: 'out_of_state' };
  if (p.includes(MOCK_TRIGGERS.outOfArea))
    return { served: false, reason: 'out_of_area' };
  if (p.includes(MOCK_TRIGGERS.unknown))
    return { served: false, reason: 'location_unknown' };
  // The token is opaque to the browser, so the mock carries the trigger forward
  // in it: `mockEstimate` has nothing else to dispatch on.
  return { ...MOCK_SERVED, served: true, addressToken: `mock-token:${p}` };
}

/**
 * Mock step 2.
 *
 * Returns only the two arms `EstimateResponse` actually has. There is no
 * `estimate_unavailable` here BECAUSE THERE IS NONE ON THE WIRE: the callable
 * collapses a Solar API outage into `solar_api_miss` for the browser, while the
 * lead it fires carries the true `estimateStatus: 'estimate_unavailable'`
 * (ADR-0026). The distinction is real but it is not the browser's to see, so a
 * third mock arm here would be inventing a response the server cannot send.
 */
export async function mockEstimate(addressToken: string): Promise<EstimateResponse> {
  if (addressToken.toLowerCase().includes(MOCK_TRIGGERS.miss)) {
    return { status: 'solar_api_miss' };
  }
  // Menands, matching the numbers ADR-0022 corrected.
  return { status: 'ok', panelsCount: 26, arrayKwDc: 11.7, annualAcKwh: 11_636.075 };
}
